import React, { useState, useEffect, useCallback } from 'react';
import { rolesApi, CreateRoleData, UpdateRoleData } from '../../api/rolesApi';
import { Role, PermissionItem, UserPermissions } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import './Roles.css';

export function Roles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<CreateRoleData>({
    name: '',
    display_name: '',
    description: '',
    permissions: {},
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rolesRes, permsRes] = await Promise.all([
        rolesApi.getRoles(),
        rolesApi.getPermissions(),
      ]);
      setRoles(rolesRes.roles);
      setPermissions(permsRes.permissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await rolesApi.createRole(formData);
      setRoles([...roles, res.role]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRole) return;
    try {
      setLoading(true);
      const updateData: UpdateRoleData = {
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions,
      };
      const res = await rolesApi.updateRole(editingRole.id, updateData);
      setRoles(roles.map(r => r.id === editingRole.id ? res.role : r));
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      setLoading(true);
      await rolesApi.deleteRole(roleId);
      setRoles(roles.filter(r => r.id !== roleId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description,
      permissions: role.permissions,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: {},
    });
  };

  const togglePermission = (key: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions?.[key as keyof UserPermissions],
      },
    }));
  };

  return (
    <div className="roles-page">
      <div className="roles-header">
        <div className="roles-title">
          <h1>Role Management</h1>
          <p>Manage roles and permissions</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Icons.Plus /> Add Role
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="roles-grid">
        {roles.map((role) => (
          <Card key={role.id} className="role-card">
            <div className="role-card-header">
              <div className="role-info">
                <h3>{role.display_name}</h3>
                <span className={`role-type ${role.is_system_role ? 'system' : 'custom'}`}>
                  {role.is_system_role ? 'System Role' : 'Custom Role'}
                </span>
              </div>
              {!role.is_system_role && (
                <div className="role-actions">
                  <button className="action-btn edit" onClick={() => openEditModal(role)}>
                    <Icons.Edit />
                  </button>
                  <button className="action-btn delete" onClick={() => handleDeleteRole(role.id)}>
                    <Icons.Trash />
                  </button>
                </div>
              )}
            </div>
            <p className="role-description">{role.description}</p>
            <div className="role-permissions">
              <h4>Permissions ({Object.values(role.permissions).filter(Boolean).length})</h4>
              <div className="permission-tags">
                {Object.entries(role.permissions)
                  .filter(([_, value]) => value)
                  .slice(0, 4)
                  .map(([key]) => (
                    <span key={key} className="permission-tag">
                      {key.replace(/_/g, ' ')}
                    </span>
                  ))}
                {Object.values(role.permissions).filter(Boolean).length > 4 && (
                  <span className="permission-tag more">
                    +{Object.values(role.permissions).filter(Boolean).length - 4} more
                  </span>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRole ? (editingRole.is_system_role ? 'View Role' : 'Edit Role') : 'Create Role'}</h2>
              <button className="modal-close" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={editingRole ? handleUpdateRole : handleCreateRole}>
              <div className="modal-body">
                <Input
                  label="Role Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  required
                  disabled={editingRole?.is_system_role}
                />
                <Input
                  label="Display Name"
                  value={formData.display_name || ''}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  disabled={editingRole?.is_system_role}
                />
                <Input
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={editingRole?.is_system_role}
                />
                <div className="permissions-section">
                  <h4>Permissions</h4>
                  <div className="permissions-grid">
                    {permissions.map((perm) => (
                      <label key={perm.key} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={!!formData.permissions?.[perm.key as keyof UserPermissions]}
                          onChange={() => togglePermission(perm.key)}
                          disabled={editingRole?.is_system_role}
                        />
                        <div className="permission-info">
                          <span className="permission-label">{perm.label}</span>
                          <span className="permission-desc">{perm.description}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="ghost" onClick={resetForm}>
                  {editingRole?.is_system_role ? 'Close' : 'Cancel'}
                </Button>
                {!editingRole?.is_system_role && (
                  <Button type="submit" isLoading={loading}>
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roles;
